import ImageIcon from "../shared/image_icon";
import UserImage from "../shared/user_image";
import useTranslation from "next-translate/useTranslation";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ONLY_TIME_FORMAT, highlightLinksInText } from "../../helpers/application";
import Image from "next/image";
import styles from "./PublicDetail.module.scss";
import { TagFill } from "react-bootstrap-icons";
import QrCode from "qrcode.react";
import { GeoAlt, Calendar } from "react-bootstrap-icons";
import AvatarList from "../groups/avatar_list";
import useWindowSize from "../../hooks/use_window_size";

dayjs.extend(utc);
dayjs.extend(timezone);

const PublicDetail = ({ event, backLink }) => {
  const { t } = useTranslation("public");
  const canvasId = "qr-gen";
  const eventPath = `/events/${event.slug}`;
  const eventURL = `${process.env.NEXT_PUBLIC_APP_URL}${eventPath}`;
  const tz = "Europe/Prague";

  const signInLink = `/sign_in?redirectTo=${eventPath}`;
  const [width, height] = useWindowSize();

  const getMaxUsers = () => {
    if (width > 1024) {
      return 7;
    } else if (width < 1024 && width > 576) {
      return 4;
    } else if (width < 576 && width > 370) {
      return 3;
    } else {
      return 2;
    }
  };

  return (
    <div className={`container mb-5`}>
      <div className="ms-1 me-1">
        <div className="mt-3 mb-3">
          <div className="d-flex">
            <Image src="/images/logo-black.svg" width={200} height={40} alt="Logo unalanu" layout="raw" />
            <div className="m-auto" />
            <a className="float-end me-1 mt-1 align-self-center" href={signInLink}>
              {t("common:profile.sign_in").toUpperCase()}
            </a>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className={styles.cardbox}>
              <div
                className={`${styles.topbar} cover-image`}
                style={{
                  backgroundImage: event.cover_image?.url
                    ? `url(${event.cover_image.url})`
                    : `url("/images/bg-${event.activity_group.identifier.toLowerCase()}.png")`,
                }}
              ></div>

              <div className="p-3">
                <h1 className={styles.h1}>{event.name}</h1>
                <div className="row mt-4">
                  <div className="col-md-6 mb-1">
                    <div className="d-flex">
                      <Calendar
                        className="align-self-center"
                        size={"4.5vw"}
                        style={{ maxHeight: 40 }}
                        alt="Calendar icon"
                      />
                      <div className="ms-1 align-self-center">
                        {dayjs.tz(event.starts_at, tz).format("dddd, D MMMM")}
                        {` @ ${dayjs.tz(event.starts_at, tz).format(ONLY_TIME_FORMAT)} - ${dayjs
                          .tz(event.ends_at, tz)
                          .tz(tz)
                          .format(ONLY_TIME_FORMAT)}`}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="d-flex">
                      <GeoAlt
                        className="align-self-center"
                        size={"4.5vw"}
                        style={{ maxHeight: 40 }}
                        alt="GeoAlt icon"
                      />
                      <span className="ms-1 align-self-center">{event.full_address}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className={`${styles.cardbox} mt-4 p-3`}>
              <h1>{t("about_event")}</h1>
              <div className="d-flex">
                {event.admin_group ? (
                  <UserImage
                    src={event.admin_group.logo_thumb}
                    name={event.admin_group.name}
                    alt={event.admin_group.name}
                    width={40}
                    height={40}
                  />
                ) : (
                  <UserImage
                    src={event.user.avatar_thumb}
                    alt={event.user.first_name}
                    name={event.user.first_name}
                    width={40}
                    height={40}
                  />
                )}
                <div className={`ms-1 badge badge-primary rounded-pill d-flex align-self-center ${styles.badge}`}>
                  <ImageIcon name="currency-black" width={20} height={20} />
                  <span className="ms-1 align-self-center">{event.is_paid ? t("common:paid") : t("common:free")}</span>
                </div>

                {event.tag && (
                  <div
                    className={`ms-1 badge badge-primary rounded-pill ${styles.badge}`}
                    style={{ backgroundColor: event.activity_group?.accent_color }}
                  >
                    <div className="d-flex align-self-center">
                      <TagFill size={19} />
                      <span className="ms-1 align-self-center">{event.tag}</span>
                    </div>
                  </div>
                )}
              </div>
              {event.description && (
                <div
                  className="mt-4 pre-line"
                  dangerouslySetInnerHTML={{ __html: highlightLinksInText(event.description) }}
                />
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className={`${styles.cardbox} mt-4 p-3`}>
              <h1>{t("join")}</h1>

              <div className="d-flex">
                <AvatarList
                  users={event.users_events.filter((ue) => ue.status === "approved").map((ue) => ue.user)}
                  showMoreLink={signInLink}
                  totalCount={event.users_events_count}
                  membersListLink={signInLink}
                  maxUsers={getMaxUsers()}
                />

                <small className={`badge badge-primary rounded-pill ${styles.badge}`}>
                  <span className="d-flex">
                    <ImageIcon name="user-black" width={20} height={20} />
                    <span className="ms-1">{`${event.users_events_count}/${event.max_participants} `}</span>
                  </span>
                </small>
              </div>

              <div className="mt-4">{t("join_text", { name: event.user.first_name })}</div>
              <div className="row justify-content-between align-items-center mt-3">
                <div className="col-6 text-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.railsformers.unalanu"
                    rel="nofollow"
                    title="Google Play"
                  >
                    <Image
                      src="/images/google-play-badge.png"
                      width={190}
                      height={58}
                      alt="Google Play store badge"
                      layout="raw"
                      style={{ width: "100%", height: "auto" }}
                    />
                  </a>
                </div>
                <div className="col-6 text-center">
                  <a href="https://apps.apple.com/us/app/unalanu/id1642908664" rel="nofollow" title="App Store">
                    <Image
                      src="/images/app-store-badge.png"
                      width={180}
                      height={60}
                      alt="App Store badge"
                      layout="raw"
                      style={{ width: "100%", height: "auto" }}
                    />
                  </a>
                </div>
              </div>

              <div className="mt-3">{t("join_text2")}</div>
              <div className="mt-3 text-center">
                <QrCode value={eventURL} id={canvasId} />

                <div className="small">
                  <a className="mt-1" href={eventURL} target="_blank" rel="noreferrer">
                    {t("open_in_app").toUpperCase()}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDetail;
